import { STATE_DRAFT } from '@opengov/stories-shared/constants';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { IPublication, IStory } from '@opengov/stories-shared/types';
import React from 'react';

import { IModalMessages } from '../../../types';
import { DeleteStoryInput, useDeleteStory } from '../../../stories/components/StoryView/hooks/useStoryMutations';

import { styles } from './DeleteStory.styles';

export type IDeleteStoryProps = {
  open: boolean;
  onClose(): void;
  isTemplate: boolean;
  story: IStory;
  publication?: IPublication;
  userId: number;
  entitySubdomain: string;
  activeTab: string;
  messages: IModalMessages;
  updatePublicationUpdatedAt(props: { publicationId: string; userId: number }): Promise<void>;
}

export const DeleteStory: React.FC<IDeleteStoryProps> = ({
  open,
  onClose,
  isTemplate,
  story,
  publication,
  userId,
  entitySubdomain,
  activeTab,
  messages,
  updatePublicationUpdatedAt,
}) => {
  const { mutate: doDeleteStory } = useDeleteStory({
    successToast: { title: messages.storyDeleted, body: messages.storyDeletedDescription },
    errorToast: { title: messages.somethingWentWrong },
  });

  if (!open) return null;

  const stateText = isTemplate ? messages.template : story.state === STATE_DRAFT ? messages.draft : messages.story;

  const handleSubmit = () => {
    if (publication?.id && userId) {
      void updatePublicationUpdatedAt({ publicationId: publication.id, userId });
    }
    onClose();
    const input: DeleteStoryInput = { isTemplate, entitySubdomain, story, activeTab, publication };
    doDeleteStory(input);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: styles.dialogPaper }} aria-labelledby="delete-story-dialog-title">
      <DialogTitle id="delete-story-dialog-title" sx={styles.dialogTitle}>
        {`${messages.deleteText} ${stateText}`}
        <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Typography variant="body1">{`${messages.deleteConfirm} ${stateText}?`}</Typography>
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button variant="outlined" color="secondary" size="small" onClick={onClose} data-test="delete-story-cancellation-button">
          {messages.cancel}
        </Button>
        <Button variant="contained" color="error" size="small" onClick={handleSubmit} data-test="delete-story-confirmation-button">
          {messages.deleteText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
